import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { LOGO_URL } from "./AdminLogin";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const todayISO = () => new Date().toISOString().split("T")[0];
const fmtDuration = (min) => (min >= 60 ? `${Math.floor(min / 60)}h${min % 60 ? `${min % 60}` : ""}` : `${min} min`);

const STATUS_LABEL = {
  confirmada: "Confirmada",
  cancelada: "Cancelada",
  concluida: "Concluída",
};

export default function ManageBooking() {
  const { token } = useParams();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [mode, setMode] = useState("view"); // view | reschedule
  const [date, setDate] = useState("");
  const [slots, setSlots] = useState([]);
  const [closed, setClosed] = useState(false);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [selectedTime, setSelectedTime] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () =>
    axios
      .get(`${API}/bookings/manage/${token}`)
      .then((res) => setBooking(res.data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    if (mode !== "reschedule" || !date || !booking) return;
    setSelectedTime("");
    setLoadingSlots(true);
    axios
      .get(`${API}/bookings/availability`, {
        params: { date, service_id: booking.service_id, staff: booking.staff },
      })
      .then((res) => {
        setSlots(res.data.slots);
        setClosed(!!res.data.closed);
      })
      .catch((err) => {
        setSlots([]);
        setClosed(false);
        toast.error(err.response?.data?.detail || "Data indisponível.");
      })
      .finally(() => setLoadingSlots(false));
  }, [date, mode, booking]);

  const cancel = async () => {
    if (!window.confirm("Tem a certeza que quer cancelar esta marcação?")) return;
    setBusy(true);
    try {
      const res = await axios.post(`${API}/bookings/manage/${token}/cancel`);
      setBooking(res.data);
      toast.success("Marcação cancelada.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Não foi possível cancelar.");
    } finally {
      setBusy(false);
    }
  };

  const reschedule = async () => {
    if (!date || !selectedTime) return toast.error("Escolha data e horário.");
    setBusy(true);
    try {
      const res = await axios.post(`${API}/bookings/manage/${token}/reschedule`, { date, time: selectedTime });
      setBooking(res.data);
      setMode("view");
      toast.success("Marcação remarcada!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Não foi possível remarcar.");
    } finally {
      setBusy(false);
    }
  };

  const Shell = ({ children }) => (
    <div className="min-h-screen bg-[#FAFAFA] flex flex-col items-center px-6 py-16">
      <img src={LOGO_URL} alt="INN.PACT" className="h-16 object-contain mb-10" />
      <div className="w-full max-w-lg bg-white border border-black/5 p-8 md:p-10">{children}</div>
      <a href="/" className="mt-8 label-editorial text-black/40 hover:text-black">Voltar ao site</a>
    </div>
  );

  if (loading) return <Shell><p className="label-editorial text-black/40">A carregar…</p></Shell>;
  if (notFound)
    return (
      <Shell>
        <p className="label-editorial text-black/50 mb-3">Marcação</p>
        <h1 className="font-display text-3xl">Marcação não encontrada</h1>
        <p className="mt-4 text-black/50 font-light">O link pode estar incorreto ou a marcação já não existe.</p>
      </Shell>
    );

  const isCancelled = booking.status === "cancelada";

  return (
    <Shell>
      <p className="label-editorial text-black/50 mb-2">A sua marcação</p>
      <h1 data-testid="manage-title" className="font-display text-3xl md:text-4xl tracking-tight">
        Olá, <span className="italic">{(booking.name || "").split(" ")[0]}</span>.
      </h1>

      <div className="mt-6 divide-y divide-black/5 text-sm">
        {[
          ["Serviço", booking.service],
          ["Profissional", booking.staff || "—"],
          ["Data", booking.date],
          ["Hora", `${booking.time} (${fmtDuration(booking.duration_min)})`],
          ["Estado", STATUS_LABEL[booking.status] || booking.status],
        ].map(([k, v]) => (
          <div key={k} className="flex justify-between py-3">
            <span className="text-black/40">{k}</span>
            <span className={`font-light ${k === "Estado" && isCancelled ? "text-red-600" : ""}`}>{v}</span>
          </div>
        ))}
      </div>

      {isCancelled ? (
        <p className="mt-8 text-black/50 font-light">
          Esta marcação foi cancelada. Para uma nova marcação, <a href="/#marcacoes" className="underline">agende aqui</a>.
        </p>
      ) : mode === "view" ? (
        <div className="mt-8 flex flex-wrap gap-3">
          <button
            data-testid="manage-reschedule-btn"
            onClick={() => setMode("reschedule")}
            className="label-editorial border border-black/20 px-6 py-3 hover:border-black transition-colors"
          >
            Remarcar
          </button>
          <button
            data-testid="manage-cancel-btn"
            onClick={cancel}
            disabled={busy}
            className="label-editorial text-red-600 border border-red-200 px-6 py-3 hover:border-red-500 transition-colors disabled:opacity-50"
          >
            Cancelar marcação
          </button>
        </div>
      ) : (
        <div className="mt-8 space-y-4">
          <div>
            <label className="label-editorial text-black/50 block mb-2">Nova data</label>
            <input
              data-testid="manage-date-input"
              type="date"
              min={todayISO()}
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="w-full bg-white border border-black/15 px-4 py-3 text-sm font-light focus:outline-none focus:border-black"
            />
          </div>
          {date && (
            <div>
              <label className="label-editorial text-black/50 block mb-2">Novo horário</label>
              {loadingSlots ? (
                <p className="text-sm text-black/40 font-light">A carregar…</p>
              ) : closed ? (
                <p className="text-sm text-black/40 font-light">Encerrado neste dia.</p>
              ) : slots.filter((s) => s.available).length === 0 ? (
                <p className="text-sm text-black/40 font-light">Sem horários disponíveis.</p>
              ) : (
                <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
                  {slots.map((slot) => (
                    <button
                      key={slot.time}
                      type="button"
                      data-testid={`manage-slot-${slot.time.replace(":", "-")}`}
                      disabled={!slot.available}
                      onClick={() => setSelectedTime(slot.time)}
                      className={`py-2 text-xs tracking-widest border transition-colors ${
                        selectedTime === slot.time
                          ? "bg-black text-white border-black"
                          : slot.available
                          ? "border-black/15 hover:border-black"
                          : "border-black/5 text-black/20 line-through cursor-not-allowed"
                      }`}
                    >
                      {slot.time}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          <div className="flex gap-3 pt-2">
            <button
              data-testid="manage-reschedule-confirm"
              onClick={reschedule}
              disabled={busy || !selectedTime}
              className="label-editorial bg-black text-white px-8 py-3 hover:bg-black/80 transition-colors disabled:opacity-50"
            >
              {busy ? "A guardar…" : "Confirmar nova data"}
            </button>
            <button onClick={() => setMode("view")} className="label-editorial text-black/50 px-4 hover:text-black">
              Voltar
            </button>
          </div>
        </div>
      )}
    </Shell>
  );
}
